import {inject, Injectable} from "@angular/core";
import {catchError, EMPTY, finalize, forkJoin, map, mergeMap, Observable, of, throwError} from "rxjs";
import {ReportingService} from "@gnv-swagger/web/secured";
import {ReportingService as ReportingCoreService} from "@gnv-swagger/core/secured";
import {BaseService} from "app/services/base.service";
import {environment} from "environments/environment";
import {HttpClient} from "@angular/common/http";
import {OntologyType} from "@gnv-shared/models";
import {extractFileNameFromContentDisposition} from "@gnv-shared/utils";
import {GetChildOfRequest$Params} from "swagger-gen/web-secured/fn/reporting/get-child-of-request";
import {SearchExportRequests$Json$Params} from "swagger-gen/web-secured/fn/reporting/search-export-requests-json";
import {UpdateExportRequest$Params} from "swagger-gen/web-secured/fn/reporting/update-export-request";
import {ExportRequestPagedList} from "swagger-gen/web-secured/models/export-request-paged-list";
import {ExportRequestModel} from "swagger-gen/web-secured/models/export-request-model";
import {ExportRequestCreate} from "swagger-gen/web-secured/models/export-request-create";
import {ExportRequestGenericOntology} from "swagger-gen/web-secured/models/export-request-generic-ontology";
import {ExportRequestTagOntology} from "swagger-gen/web-secured/models/export-request-tag-ontology";
import {ExportRequestStateOntology} from "swagger-gen/gnvert-secured/models/export-request-state-ontology";
import {ExportRequest} from "swagger-gen/web-secured/models/export-request";
import {FeedbackActions, LoaderActions} from '../../store';
import {TranslateService} from '@ngx-translate/core';
import {tap} from 'rxjs/operators';


@Injectable({
  providedIn: "root",
})
export class ExportResquestRepo extends BaseService {
  private baseUrl = environment.apiUrl;
  private fullBaseUrl = `${this.baseUrl}/export-request`;

  private readonly translate = inject(TranslateService);

  constructor(
    private reportingService: ReportingService,
    private reportingCoreService: ReportingCoreService,
    private readonly http: HttpClient
  ) {
    super();
  }

  /**
   *
   * @param params
   * @returns
   */
  public searchExportRequestChirdren = (
    params: GetChildOfRequest$Params
  ): Observable<ExportRequestPagedList> =>
    this.reportingService
      .getChildOfRequest$Response(params)
      .pipe(map((response) => response.body));

  getTemplateByUid(modelUid: string): Observable<ExportRequestModel> {
    return this.reportingService.getExportRequestModel({ uid: modelUid });
  }

  /**
   *
   * @param params Returns liltered export requests
   * @returns exportRequest Uid
   */
  public searchExportRequests = (
    params: SearchExportRequests$Json$Params
  ): Observable<ExportRequestPagedList> =>
    this.reportingService.searchExportRequests$Json({
      ...params,
      body: params.body || {},
    });

  /**
   * Create un Export Request and associated ontologies
   * @param param
   * @returns
   */
  public createExportRequestAndOntologies(param: {
    request: ExportRequestCreate;
    ontologies: {
      ontology: ExportRequestGenericOntology;
      type: OntologyType;
    }[];
    toExecute: boolean;
  }) {
    return this.reportingService
      .createExportRequest$Response({ body: param.request })
      .pipe(
        map((response) => response.headers.get("uid")),
        mergeMap((exportRequestUid) =>
          this.createOntologies(
            exportRequestUid as string,
            param.ontologies
          ).pipe(
            mergeMap(() =>
              param.toExecute
                ? this.configuredExportRequestResponse(String(exportRequestUid))
                : of(null)
            )
          )
        )
      );
  }

  /**
   * Update un Export Request and associated ontologies
   * @param param
   * @returns
   */
  public updateExportRequestAndOntologies(param: {
    request: UpdateExportRequest$Params;
    ontologies?: {
      ontology: ExportRequestGenericOntology;
      type: OntologyType;
      fullPath: string;
    }[];
    toExecute: boolean;
  }) {
    return this.reportingService
      .updateExportRequest(param.request)
      .pipe(
        mergeMap(() =>
          param.ontologies
            ? this.updateOntologies(param.request.uid, param.ontologies)
            : of(null).pipe(
              mergeMap(() =>
                param.toExecute
                  ? this.configuredExportRequestResponse(
                    String(param.request.uid)
                  )
                  : of(null)
              )
            )
        )
      );
  }

  public updateOntologies(
    exportRequestUid: string,
    ontologySummaries?: {
      ontology: ExportRequestGenericOntology;
      type: OntologyType;
      fullPath: string;
    }[]
  ) {
    if (
      !exportRequestUid ||
      !ontologySummaries ||
      ontologySummaries?.length === 0
    ) {
      return throwError(
        () => new Error("Invalid exportRequestUid or empty ontologies list")
      );
    }

    const queries$ = ontologySummaries?.map((ontologySummary) =>
      this.updateOntologyByPaths(
        ontologySummary.fullPath,
        ontologySummary.ontology
      )
    );

    // Execute all requests in parallel and return an observable that emits when all requests are completed.
    return forkJoin(queries$);
  }

  /**
   * Creates ontologies of an export request.
   *
   * @param exportRequestUid - The unique ID of the export request.
   * @param ontologySummaries
   * @returns An observable that emits the results of created ontologies.
   */
  public createOntologies(
    exportRequestUid: string,
    ontologySummaries: {
      ontology: ExportRequestGenericOntology;
      type: OntologyType;
    }[]
  ) {
    if (!exportRequestUid) {
      return throwError(() => new Error("Invalid exportRequest Uid"));
    }

    const queries$ = ontologySummaries.map((ontologySummary) =>
      this.buildOntologiQueryByType(ontologySummary, exportRequestUid)
    );

    // Execute all requests in parallel and return an observable that emits when all requests are completed.
    return forkJoin(queries$);
  }

  public deleteExportRequest(exportRequestUid: string) {
    return this.reportingService.deleteExportRequest({ uid: exportRequestUid });
  }

  private buildOntologiQueryByType(
    ontologySummary: {
      ontology:
        | ExportRequestGenericOntology
        | ExportRequestTagOntology
        | ExportRequestStateOntology;
      type: OntologyType;
    },
    exportRequestUid: string
  ) {
    switch (ontologySummary.type) {
      case OntologyType.CALL_NOTIFICATION: {
        return this.reportingService.addCallNotificationOntologyExportRequest({
          uid: exportRequestUid,
          body: ontologySummary.ontology,
        });
      }
      case OntologyType.ALARM: {
        return this.reportingService.addAlarmOntologyExportRequest({
          uid: exportRequestUid,
          body: ontologySummary.ontology,
        });
      }
      case OntologyType.EVENT: {
        return this.reportingService.addEventOntologyExportRequest({
          uid: exportRequestUid,
          body: {
            ...ontologySummary.ontology,
            tagUids: undefined,
          } as any, // TODO
        });
      }
      case OntologyType.TICKET: {
        return this.reportingService.addTicketOntologyExportRequest({
          uid: exportRequestUid,
          body: ontologySummary.ontology,
        });
      }
      case OntologyType.ANALOGIC: {
        return this.reportingService.addAnalogicOntologyExportRequest({
          uid: exportRequestUid,
          body: {
            ...ontologySummary.ontology,
            tagUids:
              (ontologySummary.ontology as ExportRequestTagOntology).tagUids ||
              undefined,
          },
        });
      }
      case OntologyType.TRANSACTION: {
        return this.reportingService.addTransactionOntologyExportRequest({
          uid: exportRequestUid,
          body: ontologySummary.ontology,
        });
      }
      case OntologyType.AVAILABILITY: {
        return this.reportingCoreService.addAvailabilityOntologyExportRequest({
          uid: exportRequestUid,
          body: ontologySummary.ontology,
        });
      }
      case OntologyType.STATION_STATE: {
        return this.reportingCoreService.addStateOntologyExportRequest({
          uid: exportRequestUid,
          body: ontologySummary.ontology as ExportRequestStateOntology,
        });
      }
      case OntologyType.UNKNOWN: {
        return EMPTY;
      }

      default:
        return EMPTY;
    }
  }

  /****************************************Rewriting the Swagger APIs******************************************************* */

  /**
   * Allows you to activate an Export requestP
   * The request status will change from INITIATED to CREATED
   * @param uid
   * @returns null
   */
  public configuredExportRequestResponse(uid: string): Observable<void> {
    const __url = `${this.fullBaseUrl}/${encodeURIComponent(
      String(uid)
    )}/configured`;
    return this.http.post<void>(__url, {}).pipe(
      catchError((error) => {
        this.dispatchFailureError(
          `An error occurred while configuring the Export Request.`
        );
        return this.handleServerError(error);
      })
    );
  }

  /**
   * Clone an Export request
   * @param uid
   * @returns uid of the new Export request
   */
  public cloneExportRequest(uid: string): Observable<string | null> {
    const __url = `${this.fullBaseUrl}/${encodeURIComponent(
      String(uid)
    )}/child`;
    return this.http.post<void>(__url, {}, { observe: "response" }).pipe(
      map((response) => response.headers.get("uid")),
      catchError((error) => {
        this.dispatchFailureError(
          `An error occurred while cloning the Export Request.`
        );
        return this.handleServerError(error);
      })
    );
  }

  /**
   * Clone an Export request
   * @param uid
   * @returns
   */
  public retryExportRequest(uid: string): Observable<void> {
    const __url = `${this.fullBaseUrl}/${encodeURIComponent(
      String(uid)
    )}/retry`;
    return this.http.post<void>(__url, {}).pipe(
      catchError((error) => {
        this.dispatchFailureError(
          `An error occurred while retrying the Export Request.`
        );
        return this.handleServerError(error);
      })
    );
  }

  /**
   * Download an Export request
   * @param uid
   * @returns
   */
  public downloadExportRequest(
    uid: string
  ): Observable<{ blob: Blob; fileName: string | undefined } | null> {
    return this.downloadExportRequestResponse(uid).pipe(
      catchError((error) => {
        this.dispatchFailureError(`An error occurred while downloading the Export Request.`);
        return this.handleServerError(error);
      })
    );
  }

  getOntologyByPaths(
    ontologyPaths: string[]
  ): Observable<
    { ontology: ExportRequestGenericOntology; fullPath: string }[]
  > {
    const queries$ = ontologyPaths.map((path) => {
      const ontologyFullPath = `${this.baseUrl}/${path}`; // It will allow this ontology to be updated.

      return this.http
        .get<ExportRequestGenericOntology | ExportRequestStateOntology>(
          ontologyFullPath
        )
        .pipe(map((ontology) => ({ ontology, fullPath: ontologyFullPath })));
    });

    return forkJoin(queries$);
  }

  updateOntologyByPaths(
    ontologyFullPath: string,
    body: ExportRequestGenericOntology | ExportRequestTagOntology
  ) {
    return this.http.put<null>(ontologyFullPath, body);
  }

  getExportRequest(exportRequestUid: string): Observable<ExportRequest> {
    return this.reportingService.getExportRequest({ uid: exportRequestUid });
  }

  /**
   * Download a result of export
   * @param uid The object ID. Must be a valid ID.
   * @return Success
   */
  downloadExportRequestResponse(
    uid: string
  ): Observable<{ blob: Blob; fileName: string } | null> {
    this.startLoader();

    return this.rustfsIsActivated()
      .pipe(
        mergeMap(isActivated => {
          return isActivated ? this.getStoredFile(uid): this.getNonStorageFile(uid)
        }),
        tap(() => this.stopLoader())
      );
  }

  private startLoader() {
    this.store.dispatch(
      LoaderActions.start({
        message: this.translate.instant("loader.fileLoading"),
      })
    );
  }

  private stopLoader() {
    this.store.dispatch(LoaderActions.stop());
  }

  getNonStorageFile(exportRequestUid: string): Observable<{blob: Blob, fileName: string}> {
    const url = `${this.fullBaseUrl}/${encodeURIComponent(exportRequestUid)}/download`;
    return this.http
      .post(url, {}, { responseType: "blob", observe: "response" })
      .pipe(
        map((response) => {
          return {
            blob: response.body as Blob,
            fileName: extractFileNameFromContentDisposition(response.headers),
          };
        })
      );
  }

  rustfsIsActivated() {
    return this.reportingService.getOntologiesRustfUingOption()
      .pipe(
        catchError(() => of(false))
      );
  }

  getStoredFile(exportRequestUid: string):Observable<{blob: Blob, fileName: string} |  null> {
    return this.getDownloadLink(exportRequestUid).pipe(
      mergeMap(response => {
        if (!response) {
          FeedbackActions.success({ message: this.translate.instant("feedback.detail.export-request-duplicate-success") });
          return of(null);
        }
        return this.getFileByDownloadLink(response?.downloadLink!)
          .pipe(
            map(file => {
              return {
                blob: file as Blob,
                fileName: response?.fileName ?? '',
              };
            })
          )
      }),
    )
  }

  getDownloadLink(exportRequestUid: string) {
    return this.reportingService.downloadExportRequestRustfs({ uid: exportRequestUid })
      .pipe(
        catchError(() => of(null))
      );
  }

  getFileByDownloadLink(downloadLink: string): Observable<Blob | null> {
    return this.http.get(downloadLink, { responseType: 'blob' })
      .pipe(
        catchError(() => of(null))
      );
  }

  public duplicateExportRequest(exportRequestUid: string): Observable<string> {
    const url = `${this.fullBaseUrl}/${encodeURIComponent(exportRequestUid)}/duplicate`;
    return this.http.post<null>(url, {}, { observe: 'response' }).pipe(
      map((response: any) => {
        const newId = response.headers.get('uid');
        return newId || ';'
      })
    );
  }
}
